import React, { useState, useEffect, useRef } from 'react';
import FormControl from './FormControl';
import classnames from 'classnames';
import { Formik, Field } from 'formik';
import { useGoogleReCaptcha } from '../recaptcha/UseGoogleRecaptcha';
import { isInViewport, scrollIntoView } from '@/utils';
import { useRouter } from 'next/router';
import { Box, Button } from '..';
import { format } from 'date-fns';

const UmbracoForm = ({
  formDefinition,
  successClass,
  submitBtnClass,
  formControlClassName,
  onSubmitSuccessful,
  children,
  isLight,
  hiddenValues,
  hiddenFieldAliases,
  redirectOverride,
  submitLabelOverride,
  ...props
}) => {
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const form = useRef();
  const GAclientId = useRef();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const setGaClientId = () => {
      try {
        // eslint-disable-next-line no-unused-vars
        const reFn = (clientId) => {
          GAclientId.current = clientId || window.gaGlobal.vid;
        };
        reFn();
        /* eslint-disable no-eval */
        eval("gtag('get', 'G-XXXXXX', 'client_id', reFn)");
      } catch (e) {
        // Most likely the error is from ga is not defined. Try until it is
        setTimeout(() => {
          setGaClientId();
        }, 100);
      }
    };
    setGaClientId();
  }, []);

  if (!formDefinition || !formDefinition.pages) {
    return null;
  }

  const initialValues = {};
  formDefinition?.pages.forEach((page) =>
    page.fieldsets.forEach((fieldset) =>
      fieldset.columns.forEach((column) =>
        column.fields.forEach((uField) => {
          if (uField.type.name === 'Multiple choice') {
            initialValues[uField.alias] = [uField.settings.defaultValue];
          } else {
            initialValues[uField.alias] = uField.settings.defaultValue;
          }
        }),
      ),
    ),
  );

  // --- Gated modal support (no-ops when the optional props are not passed) ---
  // Match caller keys to the form's aliases case-insensitively (e.g. resourceUrl -> resourceURL),
  // skip empty values and keys that don't exist on the form
  const aliasLookup = new Map(Object.keys(initialValues).map((alias) => [alias.toLowerCase(), alias]));
  const safeHiddenValues = {};
  Object.entries(hiddenValues || {}).forEach(([key, value]) => {
    const formAlias = aliasLookup.get(key.toLowerCase());
    if (!formAlias) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`UmbracoForm: hidden value "${key}" has no matching field on form "${formDefinition.name}"`);
      }
      return;
    }
    if (value !== null && value !== undefined && value !== '') {
      safeHiddenValues[formAlias] = value;
    }
  });
  Object.assign(initialValues, safeHiddenValues);

  // Only fields explicitly hidden by the caller are skipped. Umbraco 'Hidden' type fields
  // (GA ID, property, method of contact, etc.) still render through FormControl exactly as before.
  const hiddenAliasSet = new Set(
    [...(hiddenFieldAliases || []).map((key) => aliasLookup.get(key.toLowerCase()) || key)].concat(
      Object.keys(safeHiddenValues),
    ),
  );
  const isVisibleField = (uField) => !hiddenAliasSet.has(uField?.alias);
  // ---------------------------------------------------------------------------

  const getActivePageFields = () => {
    const fields = [];
    if (formDefinition?.pages[activePageIndex]) {
      formDefinition?.pages[activePageIndex].fieldsets.forEach((fieldset) =>
        fieldset.columns.forEach((column) => column.fields.forEach((uField) => fields.push(uField))),
      );
    }
    return fields;
  };

  const formDefinitionName = formDefinition.name.replace(/[\W_]+/g, '').toLowerCase();

  const validate = (values) => {
    const errors = {};

    if (formDefinition?.pages[activePageIndex]) {
      const fields = getActivePageFields();

      fields.forEach((field) => {
        if (['Date'].includes(field.type.name) && values[field.alias] != null && values[field.alias] != undefined) {
          values[field.alias] = format(values[field.alias], 'MM/dd/yyyy');
        }
        if (field.required) {
          if (['checkbox', 'Data Consent', 'Date'].includes(field.type.name)) {
            if (!values[field.alias]) {
              errors[field.alias] = field.requiredErrorMessage;
            }
          } else if (!values[field.alias] || !values[field.alias]?.trim()) {
            errors[field.alias] = field.requiredErrorMessage;
          }
        }
        if (field.pattern) {
          const re = new RegExp(field.pattern);
          if (!re.test(values[field.alias]?.trim())) {
            errors[field.alias] = field.patternInvalidErrorMessage;
          }
        }
      });
    }

    setTimeout(() => {
      const element = form.current?.querySelector('.input-group.error');

      if (element && !isInViewport(element)) {
        scrollIntoView(element, 10);
      }
    }, 0);

    return errors;
  };

  const next = (errors, setTouched) => {
    if (Object.keys(errors).length === 0) {
      setActivePageIndex(activePageIndex + 1);
    } else {
      const fields = getActivePageFields();
      const touched = {};
      fields.forEach((field) => {
        touched[field.alias] = true;
      });
      setTouched(touched);
    }
  };

  const prev = () => {
    setActivePageIndex(activePageIndex - 1);
  };

  // Returns null when there is no redirect (same as before: shows messageOnSubmit)
  const resolveRedirect = () => {
    if (redirectOverride?.url) {
      return { url: redirectOverride.url, type: (redirectOverride.type || 'content').toLowerCase() };
    }
    if (formDefinition.gotoPageOnSubmitRoute) {
      // Unchanged behaviour: form-level redirect uses router.push
      return { url: formDefinition.gotoPageOnSubmitRoute.path, type: 'content' };
    }
    return null;
  };

  const navigateTo = (redirect) => {
    if (redirect.type === 'content') {
      router.push(redirect.url);
    } else {
      // Only reachable via redirectOverride (gated modal): media (e.g. PDFs) and external links.
      // Same-tab navigation, since window.open after an async submit is blocked by popup blockers.
      window.location.assign(redirect.url);
    }
  };

  const submit = async (values) => {
    const isPreview = !!router.query.isPreview;
    setSubmitting(true);
    await executeRecaptcha('enquiryFormSubmit').then(async (gReCaptchaToken) => {
      // Gated modal only: caller-supplied hidden values win. No-op for existing forms (empty object).
      Object.assign(values, safeHiddenValues);

      if (values.gAclientID !== undefined && GAclientId.current) {
        values.gAclientID = GAclientId.current;
      }
      const submitRes = await fetch(`${!isPreview ? '/api' : ''}/umbraco/forms/api/v1/entries/${formDefinition.id}`, {
        method: 'POST',
        body: JSON.stringify({ recaptchaToken: gReCaptchaToken, values }),
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
        },
      }).catch(console.error);

      if (submitRes.ok) {
        const redirect = resolveRedirect();
        if (redirect) {
          navigateTo(redirect);
        } else {
          setSubmitted(true);
        }
        onSubmitSuccessful && onSubmitSuccessful();
        setSubmitting(false);
      } else {
        console.log(`Post Error. HTTP Response Code: ${submitRes?.status}`);
      }
    });
  };

  const submitLabel = submitLabelOverride || formDefinition.submitLabel;

  return (
    <div {...props}>
      {submitted ? (
        <div className={successClass}>{formDefinition.messageOnSubmit}</div>
      ) : (
        <Formik initialValues={initialValues} validate={validate} onSubmit={submit}>
          {({ handleSubmit, isSubmitting, validateForm, setTouched }) => (
            <form ref={form} onSubmit={handleSubmit} className="form" id={formDefinition.id}>
              <div className="flex">
                {formDefinition.pages &&
                  formDefinition.pages.map((page, pageIndex) => (
                    <div
                      key={pageIndex}
                      className={classnames(
                        'fieldsets-container w-full flex-col gap-m',
                        activePageIndex === pageIndex ? 'flex' : 'hidden',
                      )}
                    >
                      {page.fieldsets &&
                        page.fieldsets.map((fieldset) => {
                          // Only collapse columns/fieldsets that became empty because the caller hid
                          // their fields. Columns that are empty in the form definition (layout spacers)
                          // render exactly as before. With no hidden aliases this is a no-op.
                          const columns = (fieldset.columns || []).map((column) => {
                            const allFields = column.fields || [];
                            const visibleFields = allFields.filter(isVisibleField);
                            return {
                              column,
                              visibleFields,
                              hiddenByCaller: allFields.length > 0 && visibleFields.length === 0,
                            };
                          });

                          const anyHiddenByCaller = columns.some((c) => c.hiddenByCaller);
                          const anyVisibleField = columns.some((c) => c.visibleFields.length > 0);

                          if (anyHiddenByCaller && !anyVisibleField) {
                            return null;
                          }

                          return (
                            <fieldset
                              key={fieldset.id}
                              className={classnames('fieldset flex flex-col gap-8 lg:flex-row', fieldset.caption)}
                            >
                              <legend className="h-0 opacity-0">Form</legend>
                              {columns.map(({ visibleFields, hiddenByCaller }, columnIndex) =>
                                hiddenByCaller ? null : (
                                  <div key={columnIndex} className="fieldset-column flex w-full flex-col">
                                    {visibleFields.map((uField) => {
                                      return (
                                        <Field key={uField.id} name={uField?.alias}>
                                          {({
                                            field, // { name, value, onChange, onBlur }
                                            // form: { touched, errors, dirty }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
                                            meta,
                                          }) => {
                                            return (
                                              <FormControl
                                                id={uField.id}
                                                type={uField?.type?.name}
                                                label={
                                                  uField?.settings?.showLabel === 'True'
                                                    ? uField?.caption + (uField?.required ? '*' : '')
                                                    : ''
                                                }
                                                error={meta.touched && meta.error}
                                                maxLength={uField?.settings?.maximumLength}
                                                placeholder={
                                                  uField?.settings?.selectPrompt || uField?.settings?.placeholder
                                                }
                                                settings={uField?.settings}
                                                options={uField?.preValues.map(({ caption, value }) => ({
                                                  label: caption,
                                                  value,
                                                }))}
                                                required={uField?.required}
                                                disabled={isSubmitting}
                                                className={formControlClassName}
                                                formDefinitionName={formDefinitionName}
                                                {...field}
                                              />
                                            );
                                          }}
                                        </Field>
                                      );
                                    })}
                                  </div>
                                ),
                              )}
                            </fieldset>
                          );
                        })}
                    </div>
                  ))}
              </div>
              {children}
              <Box className="buttons-container mt-4 flex gap-4">
                {activePageIndex !== 0 && (
                  <button className="btn secondary" onClick={prev}>
                    {formDefinition.previousLabel}
                  </button>
                )}
                {submitting ? (
                  <div className="btn primary w-[113px]">
                    {/* eslint-disable-next-line jsx-a11y/alt-text, @next/next/no-img-element */}
                    <img
                      aria-label="loading spinner"
                      className={classnames('size-6 animate-spin', !isLight && 'invert')}
                      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAJUSURBVHgBtZZLaxNRGIa/zHRCkqop2BqhQatopeBCEQRxIaK4VXTlxp2iCxfiyj/g39CNiCLuFARBBEHEuhFEBDfiFAXvWmKbzGR8XuYMpqWdps30hYdzmZnvPfczJcvXEGyDMdgIdQgggd/wET7An+UClJapL8Nu2OxMyr7vl+M4VvCAfEBe7wx5nhd0u9135F840xUNRmE8CIJKp9PR86/uw1lo9TRA5lOwF3yI4Tm8zDPQUDQI7hP8O/lPEFm+NsFh2AcePIOn2UO/58W6a31Clz+Tiq6trHl4D22Ga0+pVNqRJInqwl4D3wWXNCQ/bPWaIXAbgynS7ZRfw1xmUK9Wq0EURbNrDJ4pJPgwPdlFOk55WmMmtEo6pL9scD0GxZqEqnqgFeG32+05S8dzUEW0foRe7CSN/GazWUbWarX+WrqBilDAXBzEwPPCMEwqlYqWYj8rpl/NsBJ9ejGq8Y8bjYYMimq99M3SE2CrNlq22Yo0kG4qpucCFx28ZunemvdsfTThDEIZTDDj2r2XrTgddQavVBgBbXGdPXUbXFvgPtyDMfXgJzxhzepCuWaD6yyNVVydqF+ySh1OOv10VFy0tesUPIDblvZkgS7hrDtA83HBVq/T8Agewsmssvc+mHbpIYyOkOo0fGNLXIOLtME16Jwr34K72cOlrszzcBWGLb0G9bIm7K39v9w1XwdgP405wfzVXENuWDrBlmcgNeEKH5/RiUi+Sz52eZnGKqueOqVajtctvQWtH4Neo+NwDCYJWiOgDkX9rmhRaFjvWM5vyz+xy72ACMlncgAAAABJRU5ErkJggg=="
                    />
                  </div>
                ) : (
                  <>
                    {activePageIndex === formDefinition.pages.length - 1 ? (
                      <Button
                        type="submit"
                        form={formDefinition.id}
                        value="Submit"
                        className={classnames(submitBtnClass || 'btn primary')}
                        text={submitLabel}
                      >
                        {submitLabel}
                      </Button>
                    ) : (
                      <div
                        type="button"
                        className="btn primary"
                        onClick={() => validateForm().then((errors) => next(errors, setTouched))}
                      >
                        {formDefinition.nextLabel}
                      </div>
                    )}
                  </>
                )}
              </Box>
            </form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default UmbracoForm;
